import { useMemo } from 'react';
import { RouterProvider } from 'react-router-dom';
import { getBrowserRouter } from 'core/routes';
import { configApp } from 'core/config';
import {
  selectMaintenanceScreenEnabled,
  selectIncidentCommunicationEnabled,
} from 'core/config/firebase/remote-config-slice';
import { FullLockdown } from 'hfs-common-ui';
import { useAppStatusConfig } from 'core/hooks/use-app-status-config';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
import { useAppSelector } from 'core/hooks/use-app-selector';
import MaintenanceScreen from 'feature/maintenance/components/MaintenanceScreen';
import PageLoader from 'core/components/PageLoader';
import MaintenanceBanner from 'core/components/MaintenanceBanner';
import ThemeWrapper from 'core/styles/ThemeWrapper';
import Clarity from '@microsoft/clarity';
import useMediaQuery from 'core/hooks/use-media-query';
import { device } from 'core/styles/variables';
import CookieBanner from 'core/layout/CookieBanner';
import useGetCookieAccepted from 'core/hooks/use-get-cookie-accepted';
import useSetUserType from 'core/hooks/use-set-user-type';
import useSetCurrentUserInfoData from 'core/hooks/use-set-user-info';
import { useSetSentryUser } from 'core/hooks/use-set-sentry-user';
import uptimeLogo from 'images/logos/maintenance_logo_utc.svg';
import backgroundImage from 'images/backgrounds/maintance_utc.jpg';
import { MaintenanceEnum } from 'core/models/maintenance.model';
import { useTranslationWrapper } from 'core/hooks/use-translation-wrapper';
import { InternationalizationBackendNameSpace } from 'core/models/internationalization-namespace';
import { Application } from 'core/models/common.model';

const App = () => {
  Clarity.init(configApp.clarity);
  const isRemoteConfigReady = useRemoteConfig();
  const appStatusConfig = useAppStatusConfig();
  const isDesktop = useMediaQuery(device.lg);
  const maintenanceScreenEnabled = useAppSelector(selectMaintenanceScreenEnabled);
  const incidentCommunicationEnabled = useAppSelector(selectIncidentCommunicationEnabled);
  // We need the isRemoteConfig to be ready before we can create the router.
  const { t } = useTranslationWrapper(InternationalizationBackendNameSpace.IncidentCommunication);

  const router = useMemo(() => getBrowserRouter(isDesktop), [isDesktop]);

  const getRoutes = () => {
    if (!incidentCommunicationEnabled && maintenanceScreenEnabled) {
      return <MaintenanceScreen />;
    }

    if (incidentCommunicationEnabled && appStatusConfig.statusMode === MaintenanceEnum.FullLockdown) {
      return (
        <FullLockdown
          title={t(`incidentCommunication.lockdown.${appStatusConfig.lockdownTitle}.title`)}
          message={t(`incidentCommunication.lockdown.${appStatusConfig.lockdownMessage}.message`, {
            name: Application.UptimeCenter,
          })}
          logo={uptimeLogo}
          icon="notification.info_outlined"
          backgroundImage={backgroundImage}
          logoAlt={Application.UptimeCenter}
        />
      );
    }

    return <RouterProvider router={router} />;
  };
  const { showCookieBanner, showCookieBannerFromLocalStorage } = useGetCookieAccepted();
  useSetUserType();
  useSetSentryUser();
  useSetCurrentUserInfoData();

  return (
    <ThemeWrapper>
      {isRemoteConfigReady ? (
        <div>
          <MaintenanceBanner statusMode={MaintenanceEnum.GlobalMaintenanceBanner} />
          {getRoutes()}
          {showCookieBanner && showCookieBannerFromLocalStorage && <CookieBanner />}
        </div>
      ) : (
        <PageLoader />
      )}
    </ThemeWrapper>
  );
};

export default App;
